/**
 * Utilitaires de formatage
 */

/**
 * SIRET lisible d'un coup d'œil : le SIREN puis le numéro d'établissement
 * (« 502 384 716 00017 »). Un numéro qui n'a pas 14 chiffres est rendu tel quel.
 * @param {string} siret
 * @returns {string}
 */
export function formatSiret(siret) {
  const digits = String(siret || '').replace(/\D/g, '')
  return digits.length === 14 ? digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4') : digits
}

/**
 * Un nombre et son nom, accordés : « 1 relancé », « 3 relancés » (zéro reste au
 * singulier, comme en français).
 * @param {number} n
 * @param {string} singulier
 * @param {string} pluriel
 * @returns {string}
 */
export const accorde = (n, singulier, pluriel) => `${n} ${n > 1 ? pluriel : singulier}`

/**
 * Formate un numéro de téléphone (français ou international)
 * @param {string} phone - Numéro de téléphone à formater
 * @returns {string} - Numéro formaté (ex: 01 23 45 67 89 ou +41 79 123 45 67)
 */
export function formatPhone(phone) {
  if (!phone) return ''

  const hasPlus = phone.trim().startsWith('+')
  const cleaned = phone.replace(/\D/g, '')

  // +33 ou 0033 → format français
  if (cleaned.startsWith('33') && cleaned.length === 11) {
    const national = '0' + cleaned.slice(2)
    return national.match(/.{1,2}/g).join(' ')
  }

  // Numéro français local (0X XX XX XX XX)
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return cleaned.match(/.{1,2}/g).join(' ')
  }

  // Numéro international : affiche avec +indicatif puis groupes de 2-3 chiffres
  if (hasPlus && cleaned.length >= 7) {
    // Détecte la longueur de l'indicatif (1 à 3 chiffres)
    const indicatifLength = cleaned.startsWith('1') && cleaned.length === 11 ? 1
      : cleaned.length <= 12 ? 2 : 3
    const indicatif = cleaned.slice(0, indicatifLength)
    const rest = cleaned.slice(indicatifLength)
    const grouped = rest.match(/.{1,2}/g)?.join(' ') || rest
    return `+${indicatif} ${grouped}`
  }

  return phone
}

/**
 * Formate un SIRET
 * @param {string} siret - SIRET à formater
 * @returns {string} - SIRET formaté (ex: 123 456 789 00012)
 */
export function formatSIRET(siret) {
  if (!siret) return ''
  const cleaned = siret.replace(/\D/g, '')
  if (cleaned.length === 14) {
    return cleaned.match(/.{1,3}/g).join(' ').trim()
  }
  return siret
}

/**
 * Formate un code postal
 * @param {string} codePostal - Code postal à formater
 * @returns {string} - Code postal formaté
 */
export function formatPostalCode(codePostal) {
  if (!codePostal) return ''
  return codePostal.replace(/\D/g, '').substring(0, 5)
}

// Formatage de montants, dates et durées déplacé vers currencyFormatters.js, dateFormatters.js et timeFormatters.js

/**
 * Tronque un texte avec ellipsis
 * @param {string} text - Texte à tronquer
 * @param {number} maxLength - Longueur maximale
 * @returns {string} - Texte tronqué
 */
export function truncate(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Capitalise la première lettre d'une chaîne
 * @param {string} str - Chaîne à capitaliser
 * @returns {string} - Chaîne capitalisée
 */
export function capitalize(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Formate un nom propre (première lettre de chaque mot en majuscule)
 * @param {string} str - Chaîne à formater
 * @returns {string} - Chaîne formatée
 */
export function formatProperName(str) {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map(word => capitalize(word))
    .join(' ')
}

/**
 * Formate un nom complet avec le nom de famille en majuscules
 * @param {string} prenom - Prénom
 * @param {string} nom - Nom de famille
 * @returns {string} - Nom formaté (ex: "Jean DUPONT")
 */
export function formatFullName(prenom, nom) {
  const prenomFormatted = prenom || ''
  const nomFormatted = nom ? nom.toUpperCase() : ''
  
  if (!prenomFormatted && !nomFormatted) return ''
  if (!prenomFormatted) return nomFormatted
  if (!nomFormatted) return prenomFormatted
  
  return `${prenomFormatted} ${nomFormatted}`.trim()
}

// Formatage de durées déplacé vers timeFormatters.js

/**
 * Labels traduits pour les types d'emails automatiques
 * Note: Les types sont déjà en français dans la base de données,
 * cette map sert à normaliser ou raccourcir certains labels si nécessaire
 */
const EMAIL_TYPE_LABELS = {
  // Types legacy (seed/test)
  confirmation: 'Confirmation',
  devis_sent: 'Devis envoyé',
  reminder: 'Rappel',
  status_update: 'Mise à jour statut',
  // Types production (quotes)
  'Confirmation de réception': 'Confirmation',
  'Devis envoyé': 'Devis envoyé',
  'Relance client - Signature devis J+7': 'Rappel J+7',
  'Relance client - Signature devis J+21': 'Rappel J+21',
  'Mise à jour de statut': 'Mise à jour',
  // Types inscription (LOT 2)
  PRESTATAIRE_DISPO_REQUEST: '2.1 — Dispo prestataire',
  DEVIS_ENVOYE_CLIENT: '2.2 — Devis client',
  PRESTATAIRE_REFUS_ALTERNATIVES: '2.3 — Refus + alternatives',
  RESERVATION_CONFIRMATION_PARTICULIER: '2.4 — Confirmation résa (particulier)',
  RESERVATION_CONFIRMATION_PRO: '2.5 — Confirmation résa (pro)',
  INSCRIPTION_CONFIRMATION_PRO: '2.6 — Confirmation inscription (pro)',
  LEARNER_CONVOCATION: '2.7 — Convocation stagiaire',
  CONVOCATION_DONNEUR_ORDRE: 'Convocation donneur d\'ordre',
  INSCRIPTION_CONFIRMATION_PARTICULIER: '2.8 — Confirmation inscription (particulier)',
  INSCRIPTION_FINALE_PRESTATAIRE: '2.9 — Inscription définitive prestataire',
  RIB_POST_FORMATION: '2.10 — RIB post-formation',
  FACTURE_ATTESTATIONS_PRO: '2.11 — Facture + attestations (pro)',
  ATTESTATION_PARTICULIER: '2.12 — Attestation (particulier)',
  POST_FORMATION_CLIENT: 'Email unifié post-formation',
  FACTURE_ACQUITTEE: 'Facture acquittée',
  // Sans libellé, la constante technique s'affichait telle quelle à l'écran
  // (« RELANCE_PAIEMENT ») dans toutes les listes de mails.
  RELANCE_PAIEMENT: 'Relance de paiement',
  NOTRE_ORGANISME_FINALISATION: '2.13 — Finalisation Notre Organisme',
  PRESTATAIRE_CONVENTION_REQUEST: 'Convention prestataire',
  PRESTATAIRE_CONVENTION_EMARGEMENT: 'Convention + émargement',
  STAGIAIRE_ESPACE_CREDENTIALS: 'Espace stagiaire',
  SESSION_CONFIRMEE_CLIENT: 'Session confirmée',
  INSCRIPTION_EXPIREE_CLIENT: 'Inscription expirée',
  COMMERCIAL_ATTENTE_STAGIAIRES: 'Liste des stagiaires à compléter',
  COMMERCIAL_ATTENTE_SIGNATURE: 'Signature attendue',
  INTERNAL_CONVENTION_ALERT_J14: 'Alerte interne — convention prestataire (J+14)',
  // Types formateur
  FORMATEUR_EMAIL_VERIFICATION: 'Vérification email',
  FORMATEUR_CONFIRMATION_RAPPEL: 'Rappel de confirmation d’adresse',
  FORMATEUR_REPRISE_INSCRIPTION: 'Reprise d’inscription',
  FORMATEUR_INSCRIPTION_RELANCE: 'Relance — inscription non finalisée',
  FORMATEUR_INSCRIPTION_RAPPEL_MENSUEL: 'Rappel mensuel — inscription non finalisée',
  FORMATEUR_CAMPAGNE_MAJ: 'Campagne — mise à jour des informations',
  FORMATEUR_CAMPAGNE_RELANCE: 'Campagne — relance',
  FORMATEUR_CONTRAT_ANNUEL: 'Contrat annuel — renouvellement',
  FORMATEUR_CONTRAT_ANNUEL_RELANCE: 'Contrat annuel — relance',
  FORMATEUR_DOCUMENTS_MANQUANTS: 'Rappel — pièces manquantes au dossier',
  FORMATEUR_URSSAF_A_RENOUVELER: 'Attestation URSSAF à renouveler',
  FORMATEUR_ADRESSE_MODIFIEE: 'Adresse de connexion changée (ancienne adresse prévenue)',
  FORMATEUR_INVITATION_MISE_A_JOUR: 'Invitation à mettre son dossier à jour',
  FORMATEUR_CONFIRMATION: 'Confirmation inscription',
  FORMATEUR_VALIDATION: 'Validation formateur',
  FORMATEUR_VALIDATED_INTERNAL_NOTIFICATION: 'Formateur validé — alerte interne',
  FORMATEUR_REJECTION: 'Rejet formateur',
  FORMATEUR_RESET_PASSWORD: 'Réinitialisation mot de passe',
  notification_nouveau_message: 'Nouveau message',
  rappel_messages_non_lus: 'Rappel de messages non lus',
  MISSION_PROPOSITION: 'Proposition mission',
  MISSION_RELANCE: 'Relance mission',
  MISSION_ATTRIBUTION: 'Attribution mission',
  MISSION_NON_RETENU: 'Non retenu mission',
  MISSION_RETIREE: 'Mission retirée',
  MISSION_MODIFIEE: 'Mission mise à jour',
  MISSION_CONFIEE_MODIFIEE: 'Mission confiée modifiée',
  MISSION_ANNULEE: 'Mission annulée',
  MISSION_REPROGRAMMEE: 'Mission reprogrammée',
  MISSION_URGENTE: 'Mission urgente',
  MISSION_DIGEST: 'Récapitulatif des nouvelles missions',
}

/**
 * Les relances d'inscription s'enregistrent sous un type composé à la volée :
 * `REMINDER_<SITUATION>_J<jours>` (cf. inscription-reminders.service.js). On
 * les reconnaît au motif plutôt que de lister les 36 combinaisons.
 */
const INSCRIPTION_REMINDER_PATTERN = /^REMINDER_([A-Z_]+)_J(\d+)$/

const INSCRIPTION_REMINDER_SITUATIONS = {
  PRESTATAIRE_NO_RESPONSE: { label: 'prestataire sans réponse', templateId: 'inscription_reminder_prestataire' },
  CLIENT_QUOTE_NOT_SIGNED: { label: 'devis non signé', templateId: 'inscription_reminder_quote_not_signed' },
  STAGIAIRE_FORM_NOT_FILLED: { label: 'formulaire non rempli', templateId: 'inscription_reminder_form_not_filled' },
  CLIENT_FORM_NOT_FINALIZED: { label: 'inscription non finalisée', templateId: 'inscription_reminder_form_not_finalized' },
  CLIENT_STAGIAIRES_NOT_FILLED: { label: 'liste des stagiaires à compléter', templateId: 'inscription_reminder_stagiaires_not_filled' },
  CLIENT_SIGNATURE_PENDING: { label: 'signature attendue', templateId: 'inscription_reminder_signature_pending' },
  CLIENT_CONVENTION_NOT_SIGNED: { label: 'convention non signée', templateId: 'inscription_reminder_convention' },
  CLIENT_NO_ALTERNATIVE_CHOSEN: { label: 'alternative non choisie', templateId: 'inscription_reminder_no_alternative' },
  PRO_PAYMENT_PENDING: { label: 'paiement en attente', templateId: 'inscription_reminder_payment' },
}

function parseInscriptionReminder(type) {
  const match = INSCRIPTION_REMINDER_PATTERN.exec(type || '')
  if (!match) return null
  const situation = INSCRIPTION_REMINDER_SITUATIONS[match[1]]
  return situation ? { ...situation, days: match[2] } : null
}

/**
 * Variantes de Badge pour les types d'emails
 */
const EMAIL_TYPE_VARIANTS = {
  // Types legacy (seed/test)
  confirmation: 'email_confirmation',
  devis_sent: 'email_devis_sent',
  reminder: 'email_reminder',
  status_update: 'email_status_update',
  // Types production (quotes)
  'Confirmation de réception': 'email_confirmation',
  'Devis envoyé': 'email_devis_sent',
  'Relance client - Signature devis J+7': 'email_reminder',
  'Relance client - Signature devis J+21': 'email_reminder',
  'Mise à jour de statut': 'email_status_update',
  // Types inscription (LOT 2)
  PRESTATAIRE_DISPO_REQUEST: 'email_status_update',
  DEVIS_ENVOYE_CLIENT: 'email_devis_sent',
  PRESTATAIRE_REFUS_ALTERNATIVES: 'email_reminder',
  RESERVATION_CONFIRMATION_PARTICULIER: 'email_confirmation',
  RESERVATION_CONFIRMATION_PRO: 'email_confirmation',
  INSCRIPTION_CONFIRMATION_PRO: 'email_confirmation',
  LEARNER_CONVOCATION: 'email_status_update',
  CONVOCATION_DONNEUR_ORDRE: 'email_status_update',
  INSCRIPTION_CONFIRMATION_PARTICULIER: 'email_confirmation',
  INSCRIPTION_FINALE_PRESTATAIRE: 'email_status_update',
  RIB_POST_FORMATION: 'email_devis_sent',
  FACTURE_ATTESTATIONS_PRO: 'email_devis_sent',
  ATTESTATION_PARTICULIER: 'email_devis_sent',
  NOTRE_ORGANISME_FINALISATION: 'email_status_update',
  PRESTATAIRE_CONVENTION_REQUEST: 'email_status_update',
  PRESTATAIRE_CONVENTION_EMARGEMENT: 'email_status_update',
  STAGIAIRE_ESPACE_CREDENTIALS: 'email_confirmation',
  SESSION_CONFIRMEE_CLIENT: 'email_confirmation',
  INSCRIPTION_EXPIREE_CLIENT: 'email_reminder',
  COMMERCIAL_ATTENTE_STAGIAIRES: 'email_status_update',
  COMMERCIAL_ATTENTE_SIGNATURE: 'email_status_update',
  INTERNAL_CONVENTION_ALERT_J14: 'email_reminder',
  // Types formateur
  FORMATEUR_EMAIL_VERIFICATION: 'email_status_update',
  FORMATEUR_CONFIRMATION_RAPPEL: 'email_reminder',
  FORMATEUR_REPRISE_INSCRIPTION: 'email_status_update',
  FORMATEUR_INSCRIPTION_RELANCE: 'email_reminder',
  FORMATEUR_INSCRIPTION_RAPPEL_MENSUEL: 'email_reminder',
  FORMATEUR_CAMPAGNE_MAJ: 'email_status_update',
  FORMATEUR_CAMPAGNE_RELANCE: 'email_reminder',
  FORMATEUR_CONTRAT_ANNUEL: 'email_devis_sent',
  FORMATEUR_CONTRAT_ANNUEL_RELANCE: 'email_reminder',
  FORMATEUR_DOCUMENTS_MANQUANTS: 'email_reminder',
  FORMATEUR_URSSAF_A_RENOUVELER: 'email_reminder',
  FORMATEUR_ADRESSE_MODIFIEE: 'email_status_update',
  FORMATEUR_INVITATION_MISE_A_JOUR: 'email_reminder',
  FORMATEUR_CONFIRMATION: 'email_confirmation',
  FORMATEUR_VALIDATION: 'email_confirmation',
  FORMATEUR_VALIDATED_INTERNAL_NOTIFICATION: 'email_status_update',
  FORMATEUR_REJECTION: 'email_reminder',
  FORMATEUR_RESET_PASSWORD: 'email_status_update',
  notification_nouveau_message: 'email_status_update',
  rappel_messages_non_lus: 'email_reminder',
  MISSION_PROPOSITION: 'email_devis_sent',
  MISSION_RELANCE: 'email_reminder',
  MISSION_ATTRIBUTION: 'email_confirmation',
  MISSION_NON_RETENU: 'email_status_update',
  MISSION_RETIREE: 'email_status_update',
  MISSION_MODIFIEE: 'email_status_update',
  MISSION_CONFIEE_MODIFIEE: 'email_status_update',
  MISSION_ANNULEE: 'email_reminder',
  MISSION_REPROGRAMMEE: 'email_status_update',
  MISSION_URGENTE: 'email_reminder',
  MISSION_DIGEST: 'email_devis_sent',
}

/**
 * Retourne le label traduit d'un type d'email
 * @param {string} type - Type d'email (confirmation, devis_sent, reminder, status_update)
 * @returns {string} - Label traduit
 */
export function getEmailTypeLabel(type) {
  if (EMAIL_TYPE_LABELS[type]) return EMAIL_TYPE_LABELS[type]
  const reminder = parseInscriptionReminder(type)
  if (reminder) return `Relance J+${reminder.days} — ${reminder.label}`
  return type
}

/**
 * Retourne la variante de Badge pour un type d'email
 * @param {string} type - Type d'email
 * @returns {string} - Variante de Badge
 */
export function getEmailTypeVariant(type) {
  if (EMAIL_TYPE_VARIANTS[type]) return EMAIL_TYPE_VARIANTS[type]
  return parseInscriptionReminder(type) ? 'email_reminder' : 'info'
}

/**
 * Mapping email_type (DB) → template ID (preview system)
 */
const EMAIL_TYPE_TO_TEMPLATE_ID = {
  // Quote — client emails
  'Confirmation de demande de devis': 'confirmation',
  'Tentative d\'appel - Nous avons essayé de vous joindre': 'call_attempt',
  'Demande de disponibilités': 'availability_request',
  'Proposition de RDV téléphonique': 'phone_appointment',
  'Devis envoyé par email': 'quote_sent',
  'Devis envoyé automatiquement (J+7)': 'quote_sent',
  'Copie du devis signé': 'quote_signed_copy',
  'Relance programmée - Rappel planifié': 'relance_scheduled',
  'Relance client - Signature devis J+7': 'client_reminder_7days',
  'Relance client - Signature devis J+21': 'client_reminder_21days',
  // Quote — admin/system emails
  'Notification - Nouvelle demande de devis': 'quote_new',
  'Notification - Nouvelle demande assignée': 'quote_assigned',
  'Notification - Demande désassignée': 'quote_unassigned',
  'Notification - Changement de statut': 'quote_status_changed',
  'Notification - Nouveau commentaire': 'comment_added',
  // Legacy/test types
  'confirmation': 'confirmation',
  'devis_sent': 'quote_sent',
  'reminder': 'client_reminder_7days',
  'status_update': 'quote_status_changed',
  'Confirmation de réception': 'confirmation',
  'Devis envoyé': 'quote_sent',
  'Mise à jour de statut': 'quote_status_changed',
  // Inscription types
  'PRESTATAIRE_DISPO_REQUEST': 'inscription_prestataire_dispo',
  'DEVIS_ENVOYE_CLIENT': 'inscription_devis_envoye',
  'PRESTATAIRE_REFUS_ALTERNATIVES': 'inscription_prestataire_refus',
  'RESERVATION_CONFIRMATION_PARTICULIER': 'inscription_reservation_particulier',
  'RESERVATION_CONFIRMATION_PRO': 'inscription_reservation_pro',
  'INSCRIPTION_CONFIRMATION_PRO': 'inscription_confirmation_pro',
  'LEARNER_CONVOCATION': 'inscription_stagiaire_convocation',
  'CONVOCATION_DONNEUR_ORDRE': 'inscription_convocation_donneur_ordre',
  'INSCRIPTION_CONFIRMATION_PARTICULIER': 'inscription_confirmation_particulier',
  'INSCRIPTION_FINALE_PRESTATAIRE': 'inscription_prestataire_final',
  'RIB_POST_FORMATION': 'inscription_rib_postformation',
  'FACTURE_ATTESTATIONS_PRO': 'inscription_facture_attestations',
  'ATTESTATION_PARTICULIER': 'inscription_attestation_particulier',
  'POST_FORMATION_CLIENT': 'inscription_post_formation_client',
  'FACTURE_ACQUITTEE': 'inscription_facture_acquittee',
  'RELANCE_PAIEMENT': 'inscription_relance_paiement',
  'NOTRE_ORGANISME_FINALISATION': 'inscription_notre_organisme',
  'PRESTATAIRE_CONVENTION_REQUEST': 'inscription_prestataire_convention',
  'PRESTATAIRE_CONVENTION_EMARGEMENT': 'inscription_prestataire_convention_emargement',
  'STAGIAIRE_ESPACE_CREDENTIALS': 'stagiaire_bienvenue',
  'SESSION_CONFIRMEE_CLIENT': 'inscription_session_confirmee',
  'INSCRIPTION_EXPIREE_CLIENT': 'inscription_expiree',
  'COMMERCIAL_ATTENTE_STAGIAIRES': 'inscription_commercial_stagiaires',
  'COMMERCIAL_ATTENTE_SIGNATURE': 'inscription_commercial_signature',
  'INTERNAL_CONVENTION_ALERT_J14': 'inscription_internal_convention_alert',
  // Formateur types
  'FORMATEUR_EMAIL_VERIFICATION': 'formateur_email_verification',
  'FORMATEUR_CONFIRMATION_RAPPEL': 'formateur_confirmation_rappel_1',
  'FORMATEUR_REPRISE_INSCRIPTION': 'formateur_reprise_inscription',
  // Les relances ne gardent pas leur rang en base : l'aperçu ouvre la
  // première, les suivantes sont à côté dans le catalogue.
  'FORMATEUR_INSCRIPTION_RELANCE': 'formateur_inscription_relance_1',
  'FORMATEUR_INSCRIPTION_RAPPEL_MENSUEL': 'formateur_inscription_rappel_mensuel',
  'FORMATEUR_CAMPAGNE_MAJ': 'formateur_campagne_maj',
  'FORMATEUR_CAMPAGNE_RELANCE': 'formateur_campagne_relance_1',
  'FORMATEUR_CONTRAT_ANNUEL': 'contrat_annuel_renouvellement',
  'FORMATEUR_CONTRAT_ANNUEL_RELANCE': 'contrat_annuel_relance_1',
  'FORMATEUR_DOCUMENTS_MANQUANTS': 'formateur_documents_manquants',
  'FORMATEUR_URSSAF_A_RENOUVELER': 'formateur_urssaf_a_renouveler',
  'FORMATEUR_ADRESSE_MODIFIEE': 'formateur_adresse_modifiee',
  'FORMATEUR_INVITATION_MISE_A_JOUR': 'formateur_invitation_mise_a_jour',
  'FORMATEUR_CONFIRMATION': 'formateur_confirmation',
  'FORMATEUR_VALIDATED_INTERNAL_NOTIFICATION': 'formateur_validated_internal_notification',
  'FORMATEUR_RESET_PASSWORD': 'formateur_reset_password',
  'notification_nouveau_message': 'message_formateur_nouveau',
  'rappel_messages_non_lus': 'message_formateur_rappel_non_lus',
  'MISSION_PROPOSITION': 'mission_recherche',
  'MISSION_RELANCE': 'mission_recherche_relance',
  'MISSION_ATTRIBUTION': 'mission_attribution',
  'MISSION_NON_RETENU': 'mission_non_retenu',
  'MISSION_RETIREE': 'mission_retiree',
  'MISSION_MODIFIEE': 'mission_recherche_modifiee',
  'MISSION_CONFIEE_MODIFIEE': 'mission_confiee_modifiee',
  'MISSION_ANNULEE': 'mission_annulee',
  'MISSION_REPROGRAMMEE': 'mission_reprogrammee',
  'MISSION_URGENTE': 'mission_urgente',
  'MISSION_DIGEST': 'mission_digest_formateur',
}

/**
 * Tous les modèles d'aperçu vers lesquels un historique d'e-mails peut
 * pointer. Sert au test qui vérifie que chacun existe bien au catalogue du
 * serveur : un lien « Aperçu » vers un modèle absent finit sur une erreur.
 */
export const EMAIL_PREVIEW_TARGET_TEMPLATE_IDS = [
  ...new Set([
    ...Object.values(EMAIL_TYPE_TO_TEMPLATE_ID),
    ...Object.values(INSCRIPTION_REMINDER_SITUATIONS).map(s => s.templateId),
  ]),
]

/**
 * Retourne le template ID de preview pour un type d'email
 * @param {string} emailType - Type d'email (valeur DB)
 * @returns {string|null} - Template ID pour la preview, ou null si non mappé
 */
export function getEmailPreviewTemplateId(emailType) {
  return EMAIL_TYPE_TO_TEMPLATE_ID[emailType] || parseInscriptionReminder(emailType)?.templateId || null
}


/**
 * Une taille de fichier, lisible : « 812 Ko », « 2,4 Mo ».
 *
 * Trois écrans en avaient besoin en même temps (le dossier de conciergerie
 * côté back-office, sa liste de fichiers côté espace formateur, et la zone de
 * dépôt) et chacun l'aurait réécrite à sa façon — c'est déjà ce qui s'est
 * produit dans `FileUploadZone`, qui garde la sienne pour les fichiers pas
 * encore envoyés.
 *
 * Les unités sont binaires (1 Ko = 1024 octets) : c'est la mesure que renvoie
 * le navigateur, et l'afficher en unités décimales ferait mentir le total sur
 * la limite de dépôt.
 *
 * @param {number} octets
 * @returns {string} taille formatée, ou une chaîne vide si la valeur manque
 */
export function formatTailleFichier(octets) {
  const valeur = Number(octets)
  if (!Number.isFinite(valeur) || valeur < 0) return ''
  if (valeur < 1024) return `${valeur} o`
  if (valeur < 1024 * 1024) return `${Math.round(valeur / 1024)} Ko`
  if (valeur < 1024 * 1024 * 1024) return `${(valeur / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`
  return `${(valeur / (1024 * 1024 * 1024)).toFixed(1).replace('.', ',')} Go`
}
